import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  newsArticleDetailSchema,
  newsArticleSchema,
  type ListNewsArticlesParams,
  type NewsArticle,
  type NewsArticleDetail,
  type NewsArticleFormValues,
} from './schema';

const BASE_PATH = '/news-articles';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(newsArticleSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: newsArticleDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: newsArticleSchema.nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

// Drop fully-empty rows; half-filled is already blocked at zod.
function toWireTranslations(values: NewsArticleFormValues) {
  return values.translations
    .filter((t) => t.title.trim().length > 0 && t.summary.trim().length > 0)
    .map((t) => ({
      localeCode: t.localeCode,
      title: t.title.trim(),
      summary: t.summary.trim(),
    }));
}

// `publishedAt` is intentionally absent — server defaults it to now() on create
// and preserves it on update (the form no longer captures it).
function toCreatePayload(values: NewsArticleFormValues) {
  return {
    kind: values.kind,
    slug: values.slug,
    title: values.title,
    summary: values.summary,
    status: values.status,
    youtubeVideoId: values.youtubeVideoId ?? null,
    imageStorageKey: values.imageStorageKey ?? null,
    thumbnailStorageKey: values.thumbnailStorageKey ?? null,
    translations: toWireTranslations(values),
    tagIds: values.tagIds,
  };
}

// `slug` is intentionally absent — immutable post-create (would 400 via forbidNonWhitelisted).
// `publishedAt` is intentionally absent — see toCreatePayload note.
function toUpdatePayload(values: NewsArticleFormValues) {
  return {
    kind: values.kind,
    title: values.title,
    summary: values.summary,
    status: values.status,
    youtubeVideoId: values.youtubeVideoId ?? null,
    imageStorageKey: values.imageStorageKey ?? null,
    thumbnailStorageKey: values.thumbnailStorageKey ?? null,
    translations: toWireTranslations(values),
    tagIds: values.tagIds,
  };
}

export async function listNewsArticles(
  params: ListNewsArticlesParams,
): Promise<CommonResponse<NewsArticle>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getNewsArticle(id: string): Promise<NewsArticleDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createNewsArticle(
  values: NewsArticleFormValues,
): Promise<NewsArticle> {
  const response = await apiClient.post<ApiResponseShape<NewsArticle>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateNewsArticle(
  id: string,
  values: NewsArticleFormValues,
): Promise<NewsArticle> {
  const response = await apiClient.put<ApiResponseShape<NewsArticle>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function deleteNewsArticle(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}
